import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import AuthModal from '../components/AuthModal';
import { isWatchlistMatch } from '../utils/watchlistMatching';

export default function Watchlist() {
  const { user, profile, updateProfile, watchlist, removeFromWatchlist, addToWatchlist, fetchWatchlist, supabase } = useAuth();
  const [notifyWatchlist, setNotifyWatchlist] = useState(profile?.notify_watchlist === true);
  const [savingPref, setSavingPref] = useState(false);

  useEffect(() => {
    setNotifyWatchlist(profile?.notify_watchlist === true);
  }, [profile?.notify_watchlist]);

  async function handleToggleNotify(e) {
    const next = e.target.checked;
    setNotifyWatchlist(next);
    setSavingPref(true);
    try {
      await updateProfile({ notify_watchlist: next });
    } catch (err) {
      setNotifyWatchlist(!next); // revert on failure
      console.error('Failed to save notification preference:', err);
    }
    setSavingPref(false);
  }
  const [showAuth, setShowAuth] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvMessage, setCsvMessage] = useState('');
  const [lbUsername, setLbUsername] = useState(() => localStorage.getItem('lbUsername') || '');
  const [lbSyncing, setLbSyncing] = useState(false);
  const [lbMessage, setLbMessage] = useState('');
  const [nowPlaying, setNowPlaying] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [yearFilter, setYearFilter] = useState('');

  // TMDB search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const searchTMDB = useCallback(async (query) => {
    if (!query || query.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`/.netlify/functions/search-tmdb?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
      } else {
        console.error('TMDB search failed:', res.status);
      }
    } catch (err) {
      console.error('TMDB search error:', err);
    }
    setSearching(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchTMDB(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery, searchTMDB]);

  // Check Now Playing
  useEffect(() => {
    if (!watchlist.length || !supabase) return;
    async function check() {
      try {
        // Paginate. There are ~21k upcoming showtimes; a single fetch
        // hits Supabase's server-side row cap (5000) and silently truncates,
        // dropping any matching showtimes whose rows fall beyond the window.
        const nowIso = new Date().toISOString();
        const showtimes = [];
        const PAGE = 1000;
        for (let from = 0; from < 100000; from += PAGE) {
          const { data, error } = await supabase
            .from('showtimes')
            .select('movie_id, cinema_movie_title, theater, start_at, booking_link')
            .gte('start_at', nowIso)
            .order('start_at', { ascending: true })
            .range(from, from + PAGE - 1);
          if (error || !data || data.length === 0) break;
          showtimes.push(...data);
          if (data.length < PAGE) break;
        }
        if (!showtimes.length) return;
        const matches = {};
        for (const item of watchlist) {
          const found = showtimes.filter(st => isWatchlistMatch({
            watchlistMovieId: item.movie_id,
            watchlistTitle: item.movie_title,
            showMovieId: st.movie_id,
            showTitle: st.cinema_movie_title || '',
          }));
          if (found.length > 0) {
            const byTheater = {};
            found.forEach(st => { if (!byTheater[st.theater] || new Date(st.start_at) < new Date(byTheater[st.theater].start_at)) byTheater[st.theater] = st; });
            matches[item.movie_title] = Object.values(byTheater).slice(0, 5);
          }
        }
        setNowPlaying(matches);
      } catch {}
    }
    check();
  }, [watchlist, supabase]);

  if (!user) {
    return (
      <div style={{ maxWidth: '600px', margin: '60px auto', textAlign: 'center', padding: '20px' }}>
        <h2 style={{ fontFamily: "'Cinzel', serif", color: '#1a1714' }}>My Watchlist</h2>
        <p style={{ color: '#6b6255', marginTop: '20px' }}>Sign in to create your watchlist and get alerts when movies you want to see are playing in NYC.</p>
        <button onClick={() => setShowAuth(true)} style={{ marginTop: '16px', padding: '10px 24px', background: '#8b6914', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600' }}>Sign In</button>
        <div style={{ marginTop: '20px' }}><Link to="/" style={{ color: '#8b6914' }}>Back to calendar</Link></div>
        <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
      </div>
    );
  }

  // Parse year from title like "Movie (2001)" or from watchlist item
  function getYear(item) {
    const m = item.movie_title.match(/\((\d{4})\)\s*$/);
    return m ? m[1] : null;
  }

  // Get unique years in watchlist
  const years = [...new Set(watchlist.map(getYear).filter(Boolean))].sort().reverse();

  // Filter watchlist by year
  const filteredWatchlist = yearFilter
    ? watchlist.filter(item => getYear(item) === yearFilter)
    : watchlist;

  function handleCsvUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvImporting(true);
    setCsvMessage('');
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n');
        const header = lines[0].toLowerCase();
        const cols = header.split(',').map(h => h.trim().replace(/^"|"$/g, ''));
        const nameIdx = cols.findIndex(h => h === 'name');
        const yearIdx = cols.findIndex(h => h === 'year');
        if (nameIdx < 0) { setCsvMessage('Invalid CSV — no "Name" column found'); setCsvImporting(false); return; }

        let added = 0;
        for (let i = 1; i < lines.length; i++) {
          // Simple CSV parsing (handles quoted fields)
          const row = lines[i].match(/(".*?"|[^,]+)/g)?.map(c => c.replace(/^"|"$/g, '').trim()) || [];
          const title = row[nameIdx] || '';
          const year = yearIdx >= 0 ? row[yearIdx] : null;
          if (!title) continue;
          const fullTitle = year ? `${title} (${year})` : title;
          try { await addToWatchlist(fullTitle, null, null); added++; } catch {}
        }
        setCsvMessage(`Added ${added} movies.`);
        fetchWatchlist(user.id);
      } catch (err) { setCsvMessage('Error: ' + err.message); }
      setCsvImporting(false);
      e.target.value = '';
    };
    reader.readAsText(file);
  }

  function handleAddFromSearch(movie) {
    const year = movie.release_date?.slice(0, 4);
    const title = year ? `${movie.title} (${year})` : movie.title;
    addToWatchlist(title, null, movie.id);
    setSearchQuery('');
    setSearchResults([]);
  }

  function toggleSelect(title) {
    setSelected(prev => { const n = new Set(prev); n.has(title) ? n.delete(title) : n.add(title); return n; });
  }

  function selectAll() {
    setSelected(new Set(filteredWatchlist.map(w => w.movie_title)));
  }

  function selectNone() { setSelected(new Set()); }

  async function deleteSelected() {
    for (const title of selected) { await removeFromWatchlist(title); }
    setSelected(new Set());
  }

  return (
    <div style={{ maxWidth: '650px', margin: '40px auto', padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <Link to="/" style={{ color: '#8b6914', textDecoration: 'none', fontSize: '0.9rem' }}>Back to calendar</Link>
      </div>
      <h2 style={{ fontFamily: "'Cinzel', serif", color: '#1a1714', marginBottom: '8px' }}>My Watchlist</h2>
      <p style={{ color: '#6b6255', fontSize: '0.85rem', marginBottom: '12px' }}>
        Add movies you want to see. We'll show you when they're screening in NYC.
      </p>

      {/* Email notification opt-in. Placed here (not in /profile) because
          this is the page describing the feature it controls. */}
      <div style={{ marginBottom: '24px', padding: '10px 12px', background: '#f3eee3', border: '1px solid #d4cec4', borderRadius: '6px' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', color: '#2a2520', fontSize: '0.9rem' }}>
          <input
            type="checkbox"
            checked={notifyWatchlist}
            onChange={handleToggleNotify}
            disabled={savingPref}
            style={{ marginRight: '10px', marginTop: '3px', cursor: 'pointer' }}
          />
          <span>
            <strong>Email me when a movie on my watchlist is playing in NYC</strong>
            <br />
            <span style={{ color: '#6b6255', fontSize: '0.82rem' }}>
              One email per day, only when there's something new. You won't be re-pinged about the same movie for 90 days.
            </span>
          </span>
        </label>
      </div>

      {/* TMDB Search */}
      <div style={{ marginBottom: '24px', position: 'relative' }}>
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search for a movie to add..."
          style={{ width: '100%', padding: '12px', border: '1px solid #d4cec4', borderRadius: '6px', fontSize: '0.95rem', background: '#fff', color: '#2a2520', boxSizing: 'border-box' }} />
        {searchResults.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #d4cec4', borderRadius: '0 0 6px 6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '300px', overflowY: 'auto' }}>
            {searchResults.map(movie => (
              <button key={movie.id} onClick={() => handleAddFromSearch(movie)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 12px', border: 'none', borderBottom: '1px solid #ede8e0', background: 'none', cursor: 'pointer', textAlign: 'left' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f5f0e8'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                {movie.poster_path ? <img src={`https://image.tmdb.org/t/p/w92${movie.poster_path}`} alt="" style={{ width: '32px', height: '48px', objectFit: 'cover', borderRadius: '3px' }} /> : <div style={{ width: '32px', height: '48px', background: '#ede8e0', borderRadius: '3px' }} />}
                <div><div style={{ color: '#2a2520', fontSize: '0.9rem', fontWeight: '500' }}>{movie.title}</div><div style={{ color: '#6b6255', fontSize: '0.8rem' }}>{movie.release_date?.slice(0, 4) || ''}</div></div>
              </button>
            ))}
          </div>
        )}
        {searching && <div style={{ position: 'absolute', right: '12px', top: '14px', color: '#6b6255', fontSize: '0.8rem' }}>Searching...</div>}
      </div>

      {/* Letterboxd auto-sync (mirror) — tested locally end-to-end, published
          on Josh's word 7/31. */}
      <div style={{ background: '#faf7f2', border: '1px solid #d4cec4', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
        <label style={{ display: 'block', fontWeight: '600', color: '#2a2520', marginBottom: '6px', fontSize: '0.9rem' }}>Sync from Letterboxd</label>
        <p style={{ fontSize: '0.8rem', color: '#6b6255', margin: '0 0 10px 0' }}>
          Enter your Letterboxd username and we'll mirror your <strong>public</strong> watchlist here — new films appear, films you removed on Letterboxd disappear. Movies you add on Film Revival itself are never touched.
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={lbUsername}
            onChange={(e) => setLbUsername(e.target.value)}
            placeholder="letterboxd username"
            style={{ padding: '8px 12px', border: '1px solid #d4cec4', borderRadius: '4px', fontSize: '0.85rem', minWidth: '180px' }}
            disabled={lbSyncing}
          />
          <button
            onClick={async () => {
              const uname = lbUsername.trim().replace(/^@/, '');
              if (!uname) { setLbMessage('Enter your Letterboxd username first'); return; }
              setLbSyncing(true);
              setLbMessage('');
              try {
                localStorage.setItem('lbUsername', uname);
                // Local testing: functions run on the local functions server
                // (npx netlify functions:serve --port 9999) — zero prod contact.
                const fnBase = window.location.hostname === 'localhost' ? 'http://localhost:9999' : '';
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();
                if (sessionError || !session?.access_token) {
                  throw new Error('Please sign in again to sync your watchlist.');
                }
                const res = await fetch(`${fnBase}/.netlify/functions/sync-letterboxd`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                  body: JSON.stringify({ username: uname }),
                });
                const j = await res.json();
                if (!res.ok) throw new Error(j.error || `sync failed (${res.status})`);
                const parts = [];
                if (j.added > 0) parts.push(`added ${j.added}`);
                if (j.removed > 0) parts.push(`removed ${j.removed}`);
                setLbMessage(parts.length
                  ? `Synced with Letterboxd: ${parts.join(', ')} (${j.letterboxdFilms} on your Letterboxd watchlist).`
                  : `You're up to date — your list matches Letterboxd (${j.letterboxdFilms} films).`);
                await fetchWatchlist(user.id);
              } catch (e) {
                setLbMessage(`Error: ${e.message}`);
              } finally {
                setLbSyncing(false);
              }
            }}
            disabled={lbSyncing}
            style={{ padding: '8px 16px', background: '#8b6914', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', opacity: lbSyncing ? 0.7 : 1 }}
          >
            {lbSyncing ? 'Syncing…' : 'Sync Watchlist'}
          </button>
        </div>
        {lbMessage && <p style={{ marginTop: '8px', fontSize: '0.8rem', color: lbMessage.startsWith('Error') ? '#cc4444' : '#4CAF50' }}>{lbMessage}</p>}
      </div>

      {/* CSV Import */}
      <div style={{ background: '#faf7f2', border: '1px solid #d4cec4', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
        <label style={{ display: 'block', fontWeight: '600', color: '#2a2520', marginBottom: '6px', fontSize: '0.9rem' }}>Import from Letterboxd</label>
        <p style={{ fontSize: '0.8rem', color: '#6b6255', margin: '0 0 10px 0' }}>
          Go to <a href="https://letterboxd.com/settings/data/" target="_blank" rel="noopener noreferrer" style={{ color: '#8b6914' }}>letterboxd.com/settings/data</a> → Export → upload your <strong>watchlist.csv</strong>.
        </p>
        <label style={{ display: 'inline-block', padding: '8px 16px', background: '#8b6914', color: '#fff', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', opacity: csvImporting ? 0.7 : 1 }}>
          {csvImporting ? 'Importing...' : 'Upload CSV'}
          <input type="file" accept=".csv" onChange={handleCsvUpload} style={{ display: 'none' }} disabled={csvImporting} />
        </label>
        {csvMessage && <p style={{ marginTop: '8px', fontSize: '0.8rem', color: csvMessage.startsWith('Error') || csvMessage.startsWith('Invalid') ? '#cc4444' : '#4CAF50' }}>{csvMessage}</p>}
      </div>

      {/* Bulk actions */}
      {watchlist.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button onClick={selectAll} style={{ background: 'none', border: '1px solid #d4cec4', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '0.8rem', color: '#2a2520' }}>Select All</button>
          <button onClick={selectNone} style={{ background: 'none', border: '1px solid #d4cec4', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '0.8rem', color: '#2a2520' }}>Select None</button>
          {selected.size > 0 && (
            <button onClick={deleteSelected} style={{ background: '#cc4444', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer', fontSize: '0.8rem' }}>
              Delete {selected.size} Selected
            </button>
          )}
          {years.length > 1 && (
            <select value={yearFilter} onChange={e => setYearFilter(e.target.value)} style={{ padding: '4px 8px', border: '1px solid #d4cec4', borderRadius: '4px', fontSize: '0.8rem', background: '#fff' }}>
              <option value="">All Years ({watchlist.length})</option>
              {years.map(y => (
                <option key={y} value={y}>{y} ({watchlist.filter(w => getYear(w) === y).length})</option>
              ))}
              <option value="none">No Year ({watchlist.filter(w => !getYear(w)).length})</option>
            </select>
          )}
        </div>
      )}

      {/* Watchlist items */}
      {filteredWatchlist.length === 0 ? (
        <p style={{ color: '#6b6255', fontStyle: 'italic', textAlign: 'center', padding: '20px' }}>
          {watchlist.length === 0 ? 'Your watchlist is empty. Search for movies above or click the heart icon on any film.' : 'No movies match this filter.'}
        </p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {filteredWatchlist.map(item => {
            const year = getYear(item);
            const titleWithoutYear = item.movie_title.replace(/\s*\(\d{4}\)\s*$/, '').trim();
            const isPlaying = nowPlaying[item.movie_title] || nowPlaying[titleWithoutYear];
            return (
              <li key={item.id} style={{ padding: '10px 0', borderBottom: '1px solid #ede8e0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" checked={selected.has(item.movie_title)} onChange={() => toggleSelect(item.movie_title)} style={{ cursor: 'pointer', flexShrink: 0 }} />
                  <span style={{ flex: 1, color: '#2a2520', fontWeight: isPlaying ? '600' : '400' }}>
                    {titleWithoutYear}
                    {year && <span style={{ color: '#6b6255', fontSize: '0.85rem' }}> ({year})</span>}
                    {item.source === 'letterboxd' && <span style={{ color: '#999', fontSize: '0.7rem', marginLeft: '6px' }}>LB</span>}
                  </span>
                  <button onClick={() => removeFromWatchlist(item.movie_title)} style={{ background: 'none', border: '1px solid #cc4444', color: '#cc4444', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontSize: '0.7rem', flexShrink: 0 }}>x</button>
                </div>
                {isPlaying && (
                  <div style={{ marginTop: '6px', marginLeft: '28px', padding: '6px 10px', background: '#e8f5e8', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                    <span style={{ color: '#2a6b2a', fontWeight: '600', fontSize: '0.75rem' }}>Now Playing!</span>
                    {isPlaying.map((st, i) => (
                      <div key={i} style={{ fontSize: '0.75rem', color: '#2a2520', marginTop: '3px', display: 'flex', justifyContent: 'space-between' }}>
                        <span>{st.theater} — {new Date(st.start_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                        {st.booking_link && <a href={st.booking_link} target="_blank" rel="noopener noreferrer" style={{ color: '#8b6914', fontSize: '0.7rem', textDecoration: 'none', fontWeight: '600' }}>Tickets</a>}
                      </div>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
